import axios from "axios";
import { buildApiUrl, encodePathValue, headersRequest } from "../helpers";

// Capa HTTP del "Tablero de Retrospectiva colaborativo" (spec 006). Molde: services/projects.js +
// components/Briefcase/BriefcaseServices.js. Todo por axios + buildApiUrl/encodePathValue/headersRequest
// (credentials incluidas por headersRequest). Los 409 de negocio (NO_VOTE_BUDGET / BOARD_CLOSED) se dejan
// propagar como excepción (axios rechaza en status >=400): el caller (Redux actions) los interpreta por
// `error.response.status` / `error.response.data.code` para el rollback optimista.

const base = (idPortafolio) => `briefcases/${encodePathValue(idPortafolio)}`;

export const RetroBoardServices = {
  // Tablero
  listBoards: async (idPortafolio) => {
    const requestUrl = buildApiUrl(`${base(idPortafolio)}/retro-tableros`);
    const result = await axios.get(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  createBoard: async (idPortafolio, payload) => {
    const requestUrl = buildApiUrl(`${base(idPortafolio)}/retro-tableros`);
    const result = await axios.post(requestUrl, payload, headersRequest(requestUrl));
    return result.data;
  },

  updateBoard: async (idPortafolio, idTablero, payload) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}`
    );
    const result = await axios.put(requestUrl, payload, headersRequest(requestUrl));
    return result.data;
  },

  closeBoard: async (idPortafolio, idTablero) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/cerrar`
    );
    const result = await axios.post(requestUrl, {}, headersRequest(requestUrl));
    return result.data;
  },

  // RF-23/CA-16 (incremento post-cierre): reabre un tablero CERRADO. Permiso restringido server-side en el
  // SP (CreadoPor del tablero o Gerente del proyecto/ciclo asociado); 403 {code:"NOT_ALLOWED_REOPEN"} si no
  // cumple, legible con getRetroErrorCode (se extiende más abajo para incluir también status 403).
  reopenBoard: async (idPortafolio, idTablero) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/abrir`
    );
    const result = await axios.post(requestUrl, {}, headersRequest(requestUrl));
    return result.data;
  },

  deleteBoard: async (idPortafolio, idTablero) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}`
    );
    const result = await axios.delete(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  // RF-25 (Incremento 2): inicia la ceremonia (timer). Mismo conjunto de permiso que reabrir
  // (CreadoPor/Gerente); 403 {code:"NOT_ALLOWED_START_TIMER"} si no cumple, 409 {code:"BOARD_CLOSED"} si
  // el tablero no está ABIERTO. Devuelve { TimerInicioUtc, TimerDuracionMinutos } para arrancar el countdown.
  startTimer: async (idPortafolio, idTablero) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/iniciar-timer`
    );
    const result = await axios.post(requestUrl, {}, headersRequest(requestUrl));
    return result.data;
  },

  // Columnas (catálogo, RF-6)
  listColumns: async (idPortafolio) => {
    const requestUrl = buildApiUrl(`${base(idPortafolio)}/retro-columnas`);
    const result = await axios.get(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  // Tarjetas
  listCards: async (idPortafolio, idTablero) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/tarjetas`
    );
    const result = await axios.get(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  createCard: async (idPortafolio, idTablero, payload) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/tarjetas`
    );
    const result = await axios.post(requestUrl, payload, headersRequest(requestUrl));
    return result.data;
  },

  updateCard: async (idPortafolio, idTablero, idTarjeta, payload) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/tarjetas/${encodePathValue(
        idTarjeta
      )}`
    );
    const result = await axios.put(requestUrl, payload, headersRequest(requestUrl));
    return result.data;
  },

  deleteCard: async (idPortafolio, idTablero, idTarjeta) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/tarjetas/${encodePathValue(
        idTarjeta
      )}`
    );
    const result = await axios.delete(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  // Reorder vertical dentro de columna (RF-10/FA-3). payload: { IdColumna, Orden: [{IdTarjeta,Orden}] }
  reorderCards: async (idPortafolio, idTablero, payload) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/reordenar`
    );
    const result = await axios.put(requestUrl, payload, headersRequest(requestUrl));
    return result.data;
  },

  // Votos (dot voting, RF-12..RF-16). 409 {code:"NO_VOTE_BUDGET"} cuando se agota el presupuesto.
  addVote: async (idPortafolio, idTablero, idTarjeta) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/tarjetas/${encodePathValue(
        idTarjeta
      )}/votos`
    );
    const result = await axios.post(requestUrl, {}, headersRequest(requestUrl));
    return result.data;
  },

  removeVote: async (idPortafolio, idTablero, idTarjeta) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/tarjetas/${encodePathValue(
        idTarjeta
      )}/votos`
    );
    const result = await axios.delete(requestUrl, headersRequest(requestUrl));
    return result.data;
  },

  // Likes (toggle, RF-17). Devuelve { estado: "LIKED"|"UNLIKED", TotalLikes }.
  toggleLike: async (idPortafolio, idTablero, idTarjeta) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/tarjetas/${encodePathValue(
        idTarjeta
      )}/like`
    );
    const result = await axios.post(requestUrl, {}, headersRequest(requestUrl));
    return result.data;
  },

  // Transparencia (RF-18): quién votó / quién dio like por tarjeta, sin N+1.
  listVoters: async (idPortafolio, idTablero) => {
    const requestUrl = buildApiUrl(
      `${base(idPortafolio)}/retro-tableros/${encodePathValue(idTablero)}/votantes`
    );
    const result = await axios.get(requestUrl, headersRequest(requestUrl));
    return result.data;
  },
};

// Extrae el `code` de negocio de un error de axios (409 NO_VOTE_BUDGET / BOARD_CLOSED, 403
// NOT_ALLOWED_REOPEN). Devuelve null si no aplica (error de red / 500 genérico / otro status).
export const getRetroErrorCode = (err) => {
  const status = err && err.response && err.response.status;
  if (status !== 409 && status !== 403) return null;
  return (err.response.data && err.response.data.code) || null;
};

export default RetroBoardServices;
